'use client'

import { partnerFormAction } from '@/lib/actions'
import { formSchema } from '@/lib/partner'
import { BlokProps } from '@/lib/types'
import { PartnerFormStoryblok } from '@/lib/types/component-types-sb'
import { zodResolver } from '@hookform/resolvers/zod'
import { useActionState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Container } from '../shared/container'
import { DynamicForm } from '../shared/dynamic-form'
import { RichText } from '../shared/rich-text'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'

function PartnerForm() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: '',
      website: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      companySize: '',
      yearsInBusiness: '',
      vendureExperience: '',
      completedProjects: '',
      services: [],
      countriesServed: [],
      portfolioLinks: '',
      additionalInfo: '',
      termsAgreed: false,
    },
  })
  const [state, formAction] = useActionState(partnerFormAction, {
    success: false,
    message: '',
  })

  return (
    <div className="space-y-4">
      {!state.success && state.message !== '' && (
        <Alert variant="destructive">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {state.message}
            {state.errors && (
              <ul className="list-disc">
                {Object.keys(state.errors).map((key) => (
                  <li key={key}>{state.errors[key]}</li>
                ))}
              </ul>
            )}
          </AlertDescription>
        </Alert>
      )}
      <DynamicForm
        action={formAction}
        form={form}
        fields={[
          // Agency Information
          {
            name: 'companyName',
            label: 'Company Name',
            type: 'text',
          },
          {
            name: 'website',
            label: 'Website',
            type: 'text',
          },

          // Contact Information
          {
            title: 'Contact Information',
            fields: [
              {
                name: 'contactName',
                label: 'Contact Name',
                type: 'text',
              },
              {
                name: 'contactEmail',
                label: 'Contact Email',
                type: 'email',
              },
              {
                name: 'contactPhone',
                label: 'Contact Phone',
                type: 'phone',
              },
            ],
          },

          // Agency Details
          {
            fields: [
              {
                name: 'companySize',
                label: 'Company Size',
                type: 'select',
                options: [
                  { label: '1-5', value: '1-5' },
                  { label: '6-20', value: '6-20' },
                  { label: '21-50', value: '21-50' },
                  { label: '51-100', value: '51-100' },
                  { label: '100+', value: '100+' },
                ],
                weight: 1,
              },
              {
                name: 'yearsInBusiness',
                label: 'Years in Business',
                type: 'number',
                weight: 1,
              },
            ],
          },

          // Vendure Experience
          {
            name: 'vendureExperience',
            label: 'Vendure Experience',
            type: 'textarea',
          },
          {
            name: 'completedProjects',
            label: 'Completed Projects',
            type: 'number',
          },

          // Services Offered
          {
            name: 'services',
            label: 'Services Offered',
            type: 'multiselect',
            options: [
              { label: 'Development', value: 'development' },
              { label: 'Design', value: 'design' },
              { label: 'Consulting', value: 'consulting' },
              { label: 'Support', value: 'support' },
              { label: 'Training', value: 'training' },
            ],
          },

          // Geographic Information
          {
            name: 'countriesServed',
            label: 'Countries Served',
            type: 'multiselect',
            options: [
              { label: 'North America', value: 'north-america' },
              { label: 'Europe', value: 'europe' },
              { label: 'Asia', value: 'asia' },
              { label: 'Africa', value: 'africa' },
              { label: 'South America', value: 'south-america' },
              { label: 'Australia/Oceania', value: 'australia-oceania' },
            ],
          },

          // Additional Information
          {
            name: 'portfolioLinks',
            label: 'Portfolio Links',
            type: 'textarea',
          },
          {
            name: 'additionalInfo',
            label: 'Additional Information',
            type: 'textarea',
          },

          // Agreement
          {
            name: 'termsAgreed',
            label: 'I agree to the privacy policy and terms of service',
            type: 'checkbox',
          },
        ]}
        submitButton={{ label: 'Apply now' }}
      ></DynamicForm>
    </div>
  )
}

export function PartnerFormSection({ blok }: BlokProps<PartnerFormStoryblok>) {
  return (
    <section className="border-border border-y py-24">
      <Container>
        <div className="mx-auto space-y-8 lg:w-[600px]">
          <RichText content={blok.content} />
          <PartnerForm />
        </div>
      </Container>
    </section>
  )
}
